import { mapActions, mapState } from 'vuex'
import BigNumber from 'bignumber.js'
import { Blockchain, BlockchainNames, EVM_BLOCKCHAIN, WalletGroup } from '@/constants/blockchain'
import { JOB_MINING } from '@/constants/routes'
import snackMixin from '@/mixins/snackMixin'
// import Transak from '@/servicies/Transak'
import { getBlockchainIcon } from '@/utils-ts/wallet'
import { formatUsdWithZeros } from '@/utils/moneyFormat'

export default {
  mixins: [snackMixin],
  props: {
    wallet: Object,
    list: {
      type: Array,
      default: () => [],
    },
    total: String,
  },
  data() {
    return {
      JOB_MINING,
      transakLoading: false,
      selectedBlockchain: Blockchain.Ethereum,
      BlockchainNames,
    }
  },
  computed: {
    ...mapState({
      email: (state) => state.user.email,
      userId: (state) => state.user.id,
      miningBalance: (state) => state.balances.miningBalance.value,
      miningBalanceIsLoaded: (state) => state.balances.miningBalance.isLoaded,
    }),
    loaded() {
      return this.wallet
    },
    isCloud() {
      return this.wallet?.group === WalletGroup.Cloud
    },
    isTronLink() {
      return this.wallet?.group === WalletGroup.TronLink
    },
    walletImage() {
      switch (this.wallet.group) {
        case WalletGroup.Metamask:
          return '/static/images/wallets/metamask-wallet.svg'
        case WalletGroup.WalletConnect:
          return '/static/images/wallets/wallet-connect.png'
        default:
          return '/static/images/wallets/cloud-wallet.svg'
      }
    },
    addressCut() {
      return `${this.wallet.address.slice(0, 6)}...${this.wallet.address.slice(-5)}`
    },
    sortedList() {
      return [...this.list].sort((a, b) => b.usd.minus(a.usd))
    },
    currencyByBlockchain() {
      const cbb = this.sortedList.reduce((map, item) => {
        const blockchain = Number(item.blockchain)
        if (blockchain === Blockchain.Tron) return map

        if (!map[blockchain]) {
          map[blockchain] = {
            blockchain: {
              name: BlockchainNames[blockchain],
              usd: new BigNumber(0),
              usdFormatted: '0',
            },
            list: [],
          }
        }

        map[blockchain].list.push(item)
        map[blockchain].blockchain.usd = map[blockchain].blockchain.usd.plus(item.usd)

        return map
      }, {})

      Object.keys(cbb).forEach((c) => {
        cbb[c].blockchain.usdFormatted = formatUsdWithZeros(cbb[c].blockchain.usd)
      })

      return cbb
    },
    blockchains() {
      return EVM_BLOCKCHAIN
    },
    blockchainOptions() {
      return this.blockchains.map((b) => ({ key: b, name: BlockchainNames[b] }))
    },
    selectedBlockchainOption() {
      return this.blockchainOptions.find((opt) => opt.key === this.selectedBlockchain)
    },
  },
  watch: {
    currencyByBlockchain: {
      handler() {
        const keys = Object.keys(this.currencyByBlockchain)
        if (keys.length && !this.currencyByBlockchain[this.selectedBlockchain]) {
          this.selectedBlockchain = Number(keys[0])
        }
      },
      immediate: true,
    },
    wallet: {
      handler() {
        if (this.wallet) {
          this.selectedBlockchain = Blockchain.Ethereum
        }
      },
      immediate: true,
    },
  },
  async created() {
    try {
      await this.loadMiningBalance()
    } catch (e) {
      console.error('Error loading mining balance', e)
      this.openSnackbar({
        type: this.SnackTypes.FAILURE,
        text: 'Error fetching Job Mining Balance. Please try again.',
      })
    }
  },
  methods: {
    formatUsdWithZeros,
    getBlockchainIcon,
    ...mapActions({
      loadMiningBalance: 'balances/loadMiningBalance',
      openModal: 'ui/openModal',
    }),
    onDepositCryptoClick() {
      this.openModal({
        component: 'lx-lazy-modal',
        props: {
          factory: import(/* webpackChunkName: "cash-modals" */ '@/modals/DepositCrypto/DepositCrypto.vue'),
          title: 'Deposit Crypto',
          props: {
            predefinedAddress: this.wallet.address,
          },
        },
      })
    },
    async onDepositCardClick() {
      this.openModal({
        component: 'lx-composite-modal-new',
        props: {
          title: 'Buy Crypto with Fiat',
          // eslint-disable-next-line max-len
          text: `<div style="white-space:pre-line">To buy cryptocurrency, you can use <a class="lx-link blue-link" href="https://transak.com/buy" target="_blank" rel="noopener nofollow">Transak</a>. Enter your internal wallet address <b style="">${this.wallet.address}</b> as the receiving address.
<b>Please note: only select tokens supported by LaborX and available on supported blockchains.</b></div>`,
        },
      })
      // if (!this.transakLoading) {
      //   try {
      //     this.transakLoading = true
      //     const transak = new Transak({
      //       walletAddress: this.wallet.address,
      //       email: this.email || this.userId,
      //     })
      //     await transak.open()
      //   } finally {
      //     this.transakLoading = false
      //   }
      // }
    },
    onWithdrawClick() {
      let predefinedCurrency
      let predefinedBlockchain
      let maxUsdValue = new BigNumber(0)

      for (const balance of this.sortedList) {
        if (balance.usd.isGreaterThan(maxUsdValue)) {
          maxUsdValue = balance.usd
          predefinedCurrency = balance.currency
          predefinedBlockchain = +balance.blockchain
        }
      }
      this.openModal({
        component: 'lx-lazy-modal',
        props: {
          factory: import(/* webpackChunkName: "cash-modals" */ '@/modals/WithdrawCryptoNew/WithdrawCryptoNew.vue'),
          title: 'Withdraw Crypto',
          props: {
            predefinedAddress: this.wallet.address,
            predefinedCurrency,
            predefinedBlockchain,
            onSuccess: ({ from, to }) => {
              this.$emit('refresh-balances', { from, to })
            },
          },
        },
      })
    },
    async onSwapClick() {
      this.openModal({
        component: 'lx-lazy-modal',
        props: {
          factory: import(/* webpackChunkName: "cash-modals" */ '@/modals/SwapNew/Swap.vue'),
          title: 'Withdraw',
          props: {
            predefinedBlockchain: this.selectedBlockchain,
            predefinedWallet: this.wallet.address,
          },
        },
      })
    },
    onSelectBlockchain(opt) {
      this.selectedBlockchain = opt.key
    },
  },
}
